import React from 'react';
import { Link } from 'react-router-dom';
import tw from "twin.macro";
import { css } from "styled-components/macro"; //eslint-disable-line

const LinkA = tw.a`underline text-secondary-100 hover:text-black`;

const translations = {
    pageInfo: {
        locationString: '/',
        title: "Gunbot - The Crypto Trading Bot for Serious Traders",
        description: 'Gunbot helps you automate trading on 20+ exchanges. Works with spot, futures and DeFi markets. Use unique trading strategies, or make your own rules.',
    },
    hero: {
        heading: "Gunbot. Community made crypto trading bot",
        description: (
            <>
            Privacy friendly crypto trading bot for 20+ supported exchanges. Works on Windows, Linux and macOS. API secrets stay on your own device.
            <br /><br />
            Use pre-tuned strategies that just work, or realize your own automation ideas. 
        </>
        ),
        notification: "Limited time: Get a free exchange slot with lifetime plans and upgrades ⚡",
        comparePlansButton: "Compare plans",
        newsAndBlogButton: "News and Blog",
        defiTradingBotButton: "DeFi trading bot",
        checkPerformanceButton: "Leaderboard",
        officialPartnersText: "Official partners",
        partnersAltText: "Official Gunbot partners: Binance, Kucoin, Okex, HitBTC, Kraken Futures and more",
        recentNewsAndArticlesText: "Recent news and articles:",
        plansLink: '/plans/',
        defiLink: '/defi-trading-bot/',
        performanceLink: '/leaderboard/',
    },
    callToAction: {
        text: "Explore how automated crypto trading with Gunbot enhances your trading strategy.",
        primaryLinkText: "Bot trading features",
        primaryLinkUrl: "/features/",
        faqLinkTitle: "Frequently Asked Questions",
        faqLinkUrl: "https://www.gunbot.com/support/faq/",
    },
    gunbotAI: {
        subheading: "Bring your trading ideas to life instantly",
        heading: "Build your own AI trading bot",
        description: `Turn your trading ideas into real strategy prototypes within seconds. Gunbot AI lets you write trading bots using simple descriptions, quickly turning your concepts into fully functional automation.

Powered by the same technology as ChatGPT, Gunbot AI understands all specific Gunbot methods and data points, empowering both experienced crypto traders and automation enthusiasts to rapidly create custom crypto trading bots.

Always test machine-generated strategies in a simulated bot environment before real trading to ensure its reliability. 

Experience what a crypto AI trading bot can do for you and transform your approach to crypto auto trading. Examples have not been edited.`,
        actionButtonText: "Explore more AI trading strategy examples",
        actionButtonURL: "https://www.gunbot.com/support/docs/custom-strategies/example-strategies/overview/",
    },
    how: {
        text: "Start automated cryptocurrency trading with Gunbot in just a few minutes. It's quick and easy to get set up.",
        primaryLinkText: "How Gunbot works",
        primaryLinkUrl: "/how-gunbot-works/",
        primaryLinkTitle: "How to install Gunbot"
    },
    pricingModule: {
        heading: 'Get started with the trusted crypto trading bot',
        subheading: '',
        description: "Select billing cycle:",
        primaryButtonText: "I want this!",
        yearlyAdmonition: '-50%',
        lifetimeAdmonition: 'Best value',
        tabs: {
            Monthly: [],
            Annual: [],
            Lifetime: [],
        },
        plansDescription: <p className={"description"}>Did you know?<br /><br />Yearly plans save you 50% 😉</p>,
        plansDescriptionAnnual: <p className={"description"}>Did you know?<br /><br />Lifetime plans offer even greater savings 😉</p>,
        plansDescriptionLifetime: <p className={"description"}>Did you know?<br /><br />Pay once and enjoy lifetime usage 😎</p>,
        showYearlyPlans: "Show Yearly Plans",
        showLifetimePlans: "Show Lifetime Plans",
        showMonthlyPlans: "Show Monthly Plans",
        joinGunbotCommunity: "Join Gunbot community",
        moreDetailsAbout: "More details about",
        planFeatures: [
            "Feature 1 description",
            "Feature 2 description",
        ],
    },
    features: {
        heading: "Why we think we've built the best crypto trading bot software",
        cards: [
            {
                title: "Advanced Trading Strategies",
                description: "Access 20+ thoroughly tested trading strategies. Fine-tune them with over 150 configurable parameters to match your trading style."
            },
            {
                title: "Trade Unlimited Pairs",
                description: "Trade without restrictions. Launch your cryptocurrency trading bot on one or hundreds of markets simultaneously — the choice is yours."
            },
            {
                title: "Optimized Trailing",
                description: "Improve your trading outcomes with customizable trailing options. Trades trigger only when prices stabilize, increasing precision."
            },
            {
                title: "Enhanced Indicators",
                description: "Integrate popular indicators like ADX, RSI, Stochastic, StochRSI, MFI, EMA, EMAspread, and more to refine your entry and exit points."
            },
            {
                title: "Dynamic AutoConfig",
                description: "Adapt to market shifts automatically. Gunbot is the only trading bot offering rule-based dynamic configurations — use built-in filter rules or create your own."
            },
            {
                title: "Visual Trading Targets",
                description: "Clearly visualize your trading strategy goals using the integrated TradingView charts. Fine-tune strategies directly from the chart view."
            }
        ]
    },
    usps: {
        subheading: "Next-generation trading automation",
        heading: "Most flexible trading bot for crypto",
        description: (
            <span>
                Enhance your crypto trading capabilities with Gunbot — the automated trading bot for crypto traders and automation experts. Use proven strategies or easily craft your own trading algorithms.
            </span>
        ),
        imageAlt: "Quickly build a custom bot trading strategy",
        features: [
            {
                title: "Total Control: Your Bot, Your Privacy",
                description: (
                    <span>
                        Unlike cloud-based crypto trading bots, Gunbot runs locally on your device. Maintain total control and privacy on Windows, macOS, Linux, or Raspberry Pi.
                    </span>
                ),
            },
            {
                title: "Instant Plug and Play Bot Trading Strategies",
                description: (
                    <span>
                        Activate pre-tuned <LinkA href={"https://www.gunbot.com/support/guides/trading-logic-and-optimization/selecting-spot-strategies/"} title="How to select a Gunbot strategy">trading algorithms</LinkA> like stepgridscalp in moments. Gunbot starts trading immediately, and even supports leveraged trades on futures/derivatives platforms.
                    </span>
                ),
            },
            {
                title: "Unlimited Customization",
                description: (
                    <span>
                        We love our power users. Launch <LinkA href={"https://www.gunbot.com/support/faq/multiple-bot-instances/"} title="Why run multiple gunbot instances">unlimited bot instances</LinkA>, create personalized strategies, and automate configuration changes effortlessly. Enjoy endless flexibility with our multi-platform trading bot. Gunbot also has a full API.
                    </span>
                ),
            },
            {
                title: "Lifetime License, Free Updates",
                description: "Gunbot includes free updates even for lifetime licenses. Pay once, enjoy continuous enhancements, or choose a subscription if lifetime isn't your choice yet.",
            }
        ],
        learnMoreLinkText: "Developer resources",
        learnMoreLinkURL: "/devcommunity/"
    },
    grid: {
        subheading: "Easy-to-use multi timeframe spot and futures bots",
        heading: "Advanced Grid and DCA Trading Bots",
        description: (
            <span>
                Gunbot's smart grid strategies redefine trading. Unlike typical gridbots, Gunbot offers smarter controls:
                <br /><br />
                <strong>Easy Limit Gridbots:</strong> Simple-to-use gridbot with limit orders, custom grid sizes, and auto-rebalancing.<br /><br />
                <strong>Built-in Trend Detection:</strong> Avoid risky market conditions with automatic trend analysis.<br /><br />
                <strong>Dynamic Targeting:</strong> Targets dynamically adjust according to recent volatility, optimizing returns.<br /><br />
                <strong>Price Trailing:</strong> Improve fill rates significantly using Gunbot's advanced trailing options - even in grid strategies.<br /><br />
                If you're more into TA or DCA trading, Gunbot got you covered too. Our strategies cover the full spectrum.
            </span>
        ),
        imageAlt: "Grid trading bot strategy featuring automated DCA and profit targets",
    },
    testimonialsData: [
        {
            customerName: "Didier",
            quote:
                "Bought Gunbot in November, fumbled with Sim in December... Kicked off real trading last month... And after 1 month I made back the ultimate lifetime licence cost 2 times. Thanks for your great software @GuntharDeNiro and to all those who helped make it stronger with new strategies and feedback for improving the systems over the years. This is the best bot I have seen in terms of control we have as a user."
        },
        
        {
            customerName: "Ed",
            quote:
                "I did a substantial amount of research before purchasing automated trading software and I can confidently say I have zero regrets going with Gunbot. I come from a manual trading background, and the support network here is absolutely insane in terms of helpfulness—the response time is faster than what you would get from your wife! This product deserves a 5 star review ALL DAY LONG."
        },
        {
            customerName: "Nikita",
            quote:
                "Would definitely give Gunbot 5 stars. Because even for someone like me, who is a complete beginner to the world of trading bots, and will obviously struggle with the typical 'newbie' error codes associated with setting up a new software that you are not familiar with. Their fast responding support team and wiki were more than enough to solve theseissues."
        },
        {
            customerName: "Robert",
            quote:
                "Look, use stepgridscalp leave default and make money. [...] This community is fantastic and I will testify it is not a scam. Been a part of it since 2017. Making big fucking money with it."
        },
        {
            customerName: "Mike",
            quote:
                "Trailing stops are great and all but let’s be honest, on markets that run 24/7 more is required. Much more. GunBot was something I wished for a long time and my only regret is not doing it sooner. The bot itself has been awesome so far, and the support has been even better. Very fast responses, super knowledgeable, and friendly."
        },
        {
            customerName: "Lebowski",
            quote:
                "Major love to the GB team. I started trading on DYDX using SGSFutures on Feb 26th. Started with $496 USD and I'm currently at $1,358 USD today in portfolio value. Running on 20 pairs. :)"
        },
        
        {
            customerName: "Mike",
            quote:
                "It’s extremely intimidating to allow a bot to control large sums of your money. Support had me go live in two days from payment to first trade. I’m sitting here writing this, watching my daughter play, bot streaming in background, and I finally feel like I can look away from my screen for more than 10 minutes."
        },
        {
            customerName: "EMC",
            quote:
                "I bought Gunbot back in the end of 2017, traded a bit, but then other things came in the way so I put it on ice. Recently I wrote a message in the contact form, and after like 20 minutes I got an email. They helped me get the bot back up running almost instantly. I did not expect such customer service after 3 years of owning a crypto bot. Gunbot withstands the test of time!"
        },
        {
            customerName: "Jan",
            quote:
                "Outstanding Trading Bot with Robust Features and Reliability! Gunbot has consistently exceeded my expectations."
        },
        {
            customerName: "Stefan",
            quote:
                "Running so good out of the box I haven’t really looked into all switches and details"
        },
        {
            customerName: "Daniel",
            quote:
                "Its making money out of the box just after editing balance settings, I have a plan to use it for scalping using TA, turning it on or off depending on the market, but so far it is great."
        },
        {
            customerName: "Nenad",
            quote:
                "StepGridHedge worked like a charm in this up/down market, so far best from out of the box strategy, fire and forget and drink mojitos :)"
        },
        {
            customerName: "Dave",
            quote:
                "StepGrid has become default to beat (and pretty hard)"
        },
        {
            customerName: "B B",
            quote:
                "After being quite suspicious I have quite good results with the spotgrid. Did not understand it properly in the first place - but now got the hang of it and it works quite well."
        },
        {
            customerName: "ingo",
            quote:
                "It's a great product. I spent about a year working on my own bot in Python before discovering Gunbot. It turned out to be exactly what I envisioned for my 'dream bot,' but far more advanced and mature. That's why I consider it an excellent product that is worth every dollar. Thank you very much, and Merry Christmas!"
        },
        {
            customerName: "isayyas",
            quote:
                "BTW, your support is mind-blowing, but watching your baby bot grow for a few years now is a whole other game. You guys are absolutely the best!"
        },
        {
            customerName: "puffncough",
            quote:
                "Having purchased Gunbot back in 2017, and using it on and off throughout the years, I am always impressed by the improvements made here internally and through the community. I rarely have to ask questions because I can find what I need on the wiki or through the telegram channel. You guys are amazing and thank you."
        },
        {
            customerName: "Schurli",
            quote:
                "The strats in the last year have been just crazy. The competition and especially the top 5 each time are just completely insane. The last year for Gunbot was unbelievable. If we had what we have right now, with no changes or updates, we would be already golden. Those strats just keep getting better and more refined. I never felt more confident in recommending Gunbot to a friend than now."
        },
        {
            customerName: "Walton",
            quote:
                "I’ve been using Gunbot for quite some time, and it has been a solid trading tool for me. The interface is easy enough to pick up, but it’s definitely not for complete beginners with no trading experience. You still need to think things through and apply your own knowledge to get the best results from its many features. In terms of performance, it has been consistent, and I’ve rarely run into issues. When I did have questions, the support team responded quickly and helped me out. If you know your way around trading and want a bot that offers flexibility, Gunbot is a good choice."
        },
        {
            customerName: "Siggen",
            quote:
                "Great peace of Software. Always evolving. New features added all the time without additional cost. Amazing community helping each other out with trading and other stuff."
        },
        {
            customerName: "Eden",
            quote:
                "My friend and I have been comparing Eazybot vs Gunbot. Gunbot is definitely better and more flexible in the long run. In addition, so far it is winning the comparisons we've been doing."
        },
        {
            customerName: "Jon",
            quote:
                "I look around and see so many talented people working on their own great strategies. It's very heartening to be with people who want the same."
        },
        {
            customerName: "Murtis",
            quote:
                "I am very thankful for everyone here—a fun little community! The difference since 5 years ago would be difficult to even explain to new users. There are some extremely talented/ingenious people here."
        },
        {
            customerName: "Dave",
            quote:
                "I knew manual trading pretty good but GB was the catalyst to learn how to codify that."
        },
        {
            customerName: "Đ₹ɄṂ₮Ḗ₡Ħ",
            quote:
                "I remember the time I came to Gunbot from Haasonline; I have made maybe the rightest choice in my life."
        },
        {
            customerName: "Wissam",
            quote:
                "Best Software of its kind. I have been using Gunbot for almost a year now; its performance is remarkable and automation makes it a real money maker with added peace of mind. The team is very supportive and the documentation is excellent. Still more valuable features can be added that I am looking forward to see."
        }
    ],
    why: {
        cards: [
            {
                title: "Privacy-Focused Trading",
                description: "Gunbot values your privacy. Unlike typical bot trading platforms, Gunbot never tracks your trading data. The only exception to this is our voluntary leaderboard, which needs minimal data collection only used for the leaderboard."
            },
            {
                title: "Combine Manual and Bot Trading",
                description: <>
                    Seamlessly <LinkA href={"https://www.gunbot.com/support/faq/difference-automated-manual-trading-gunbot/"} title="Combine automated and manual trading">combine</LinkA> manual trading with bot trading.  You can manually add to positions opened by Gunbot, or sell (parts of) an existing position. Gunbot will keep track of the price you can safely exit, even after a complex series of trades. See <Link to="/use-cases/" title="Gunbot use cases">examples</Link>.
                </>
            },
            {
                title: "Dedicated Support",
                description: <>
                    Receive quick, helpful <LinkA href="/support/" title="Gunbot support community">human support</LinkA>, detailed documentation, and community insights anytime.
                </>
            }
        ],
        heading: "Why Traders Prefer Gunbot",
        subheading: "",
        description: `Imagine bot crypto trading without constraints – Gunbot empowers you to seize profit opportunities around the clock. With Gunbot, your crypto trading never sleeps.`,
    },
    charts: [
        { id: 1, title: 'Use unique trading bot strategies', description: 'Automate with built-in strategies, or design your own unique approach.' },
        { id: 2, title: 'Trade smarter on multiple timeframes', description: 'Strategies adapt to trends across various timeframes for smarter trading decisions.' },
    ],
    // EN only
    related: {
        title: "Related topics",
        introText: "Explore our guides and articles to learn more about Gunbot.",
        articles: [
            { title: "What is the Gunbot Dev Community?", url: "https://www.gunbot.com/devcommunity/" },
            { title: "Quick start guide", url: "https://www.gunbot.com/support/guides/getting-started/gunbot-quickstart-guide/" },
            { title: "Are There Any Additional Costs Involved in Using Gunbot?", url: "https://www.gunbot.com/support/faq/additional-costs-gunbot/" },
            { title: "Spot trading bot strategy options", url: "https://docs.gunthy.org/guides/trading-logic-and-optimization/selecting-spot-strategies/" },
            { title: "How Frequently Does Gunbot Release Updates?", url: "https://www.gunbot.com/support/faq/gunbot-update-frequency/" },
        ],
    }
};


export default translations;